import type { CardInfo, Scan, ScanConfirmOptions } from "@hobbyshopify/backend-client";

/**
 * The confirm form's state and what it sends. Pure: the controller owns an
 * `EditState`, the form edits it, and these functions turn it into requests.
 *
 * The identity fields decide **which card this is**; they are the identity a
 * `Card` is keyed on, so editing one is what makes a corrected scan attach to
 * the right card (or create a new one) instead of quietly joining the wrong
 * one's price history.
 */
export type EditState = {
  // ── identity ──
  player: string;
  team: string;
  brand: string;
  year: string;
  cardNumber: string;
  parallelType: string;
  insertName: string;
  cardType: string;
  // Free-form, exactly as the model writes it: "PSA 10", "9.5", "raw".
  // scan-core's normalizeGrade parses it into (company, grade, slabbed), which
  // is why this stays one text field rather than three controls.
  grade: string;
  slabbed: boolean;
  // ── description ──
  cardFullName: string;
  setName: string;
  // ── economics, in dollars as typed ──
  price: string;
  floorPrice: string;
};

export function centsToInput(cents: number | null | undefined): string {
  return cents == null ? "" : (cents / 100).toFixed(2);
}

/** Dollars as typed, in cents. A comma decimal ("12,50") reads as a point; blank or not a number is undefined. */
export function inputToCents(value: string): number | undefined {
  const typed = value.trim().replace(/,/g, ".");
  const parsed = Number(typed);
  if (!typed || !Number.isFinite(parsed)) return undefined;
  return Math.round(parsed * 100);
}

/** Blank is valid (it takes the card off sale); anything else must read as a number. */
export const isMoneyValid = (value: string) => !value.trim() || inputToCents(value) !== undefined;

/**
 * The figure a scan's review starts from: a matched card's own price, since
 * accepting a match keeps what the shop already decided, else the pipeline's
 * estimate, which is the number the reviewer is asked to confirm or correct.
 */
export const prefillPriceCents = (scan: Scan) => scan.card?.currentPriceCents ?? scan.estimatedPriceCents;

export function toEditState(scan: Scan): EditState {
  const info = scan.cardInfo;
  const card = scan.card;
  return {
    player: info?.player ?? "",
    team: info?.team ?? "",
    brand: info?.brand ?? "",
    year: info?.year != null ? String(info.year) : "",
    cardNumber: info?.card_number ?? "",
    parallelType: info?.parallel_type ?? "",
    insertName: info?.insert_name ?? "",
    cardType: info?.card_type ?? "",
    grade: info?.grade ?? "",
    slabbed: info?.slabbed ?? false,
    cardFullName: info?.card_full_name ?? "",
    setName: info?.set_name ?? "",
    price: centsToInput(prefillPriceCents(scan)),
    // One figure for both until the shop sets a floor of its own, as iOS reads it.
    floorPrice: centsToInput(card?.floorPriceCents ?? prefillPriceCents(scan)),
  };
}

/**
 * The form after a change. The floor follows the asking price for as long as
 * the two agree, and a floor typed above the asking price raises it to meet
 * it. A port of the iOS `AdminScanEditForm` price and floor.
 */
export function withEdit(prev: EditState, updates: Partial<EditState>): EditState {
  const next = { ...prev, ...updates };
  if (
    updates.price !== undefined &&
    updates.floorPrice === undefined &&
    inputToCents(prev.floorPrice) === inputToCents(prev.price)
  ) {
    next.floorPrice = next.price;
  }
  if (updates.floorPrice !== undefined) {
    const floor = inputToCents(next.floorPrice);
    if (floor !== undefined && floor > (inputToCents(next.price) ?? 0)) next.price = next.floorPrice;
  }
  return next;
}

export const MIN_YEAR = 1800;
export const MAX_YEAR = new Date().getFullYear();

/** Empty is allowed (the model may not have read a year); otherwise 1800..this year. */
export function isYearValid(year: string): boolean {
  if (!year) return true;
  if (!/^\d{4}$/.test(year)) return false;
  const parsed = Number(year);
  return parsed >= MIN_YEAR && parsed <= MAX_YEAR;
}

/** A floor above the asking price would refuse every sale it is meant to allow. */
export function isFloorValid(edit: EditState): boolean {
  const price = inputToCents(edit.price);
  const floor = inputToCents(edit.floorPrice);
  if (price == null || floor == null) return true;
  return floor <= price;
}

/**
 * Just the reading half: what this edit changes about the card itself.
 *
 * Diffed against `scan.cardInfo`, which is the reading *in force* including any
 * draft already saved, so reverting a field back to what the model said sends
 * that value rather than silently leaving the earlier edit in place. Only the
 * fields the reviewer actually changed: sending the whole reading back would
 * make every confirm look like a correction, and `matchedCardId <> cardId`,
 * the matcher's measured error rate, would stop meaning anything.
 *
 * Empty when nothing changed, which is what lets a caller skip the request.
 */
export function buildCorrections(scan: Scan, edit: EditState): Partial<CardInfo> {
  const info: Partial<CardInfo> = scan.cardInfo ?? {};
  const corrections: Record<string, unknown> = {};

  // A field the model did not read compares as the form's own empty value for
  // its type, so an untouched checkbox is not sent as a correction.
  const put = (key: keyof CardInfo, was: string | number | boolean | undefined, now: string | number | boolean) => {
    const empty = typeof now === "boolean" ? false : "";
    if ((was ?? empty) !== now) corrections[key] = now;
  };

  put("player", info.player, edit.player);
  put("team", info.team, edit.team);
  put("brand", info.brand, edit.brand);
  put("card_number", info.card_number, edit.cardNumber);
  put("parallel_type", info.parallel_type, edit.parallelType);
  put("insert_name", info.insert_name, edit.insertName);
  put("card_type", info.card_type, edit.cardType);
  put("grade", info.grade, edit.grade);
  put("slabbed", info.slabbed, edit.slabbed);
  put("card_full_name", info.card_full_name, edit.cardFullName);
  put("set_name", info.set_name, edit.setName);
  if (edit.year && Number(edit.year) !== info.year) corrections.year = Number(edit.year);

  return corrections as Partial<CardInfo>;
}

/**
 * The corrections that change which card this is, and so its comps: they wait
 * for "Save and reprice". The grade is left out, since it picks a comp of the
 * same card rather than a new card.
 */
export function identityCorrections(scan: Scan, edit: EditState): Partial<CardInfo> {
  const { grade: _grade, slabbed: _slabbed, ...identity } = buildCorrections(scan, edit);
  return identity;
}

/**
 * Whether two readings are the same card, as far as its comps go. Asked both
 * ways, since a correction names only the fields it fills: a year one reading
 * has and the other lacks shows from one side alone.
 */
export function sameIdentity(a: Scan, b: Scan): boolean {
  return (
    JSON.stringify(identityCorrections(a, toEditState(b))) === "{}" &&
    JSON.stringify(identityCorrections(b, toEditState(a))) === "{}"
  );
}

/** What the reading holds; the price and floor follow rules of their own. */
const READING_FIELDS = [
  "player",
  "team",
  "brand",
  "year",
  "cardNumber",
  "parallelType",
  "insertName",
  "cardType",
  "grade",
  "slabbed",
  "cardFullName",
  "setName",
] as const satisfies readonly (keyof EditState)[];

/**
 * The form over the scan the server answered a request with. A field the
 * reviewer left as the old scan read it, both in the request and since, takes
 * the answer's value: a pick writes the chosen product's catalog identity, which
 * the form must show rather than send the old product's back as a correction.
 * `touched` names fields an earlier request changed whose answer never came.
 */
export function rebasedEdit(
  form: EditState,
  sent: EditState,
  was: Scan,
  now: Scan,
  touched: ReadonlySet<string> = new Set(),
): EditState {
  const before = toEditState(was);
  const after = toEditState(now);
  const out: Record<string, unknown> = { ...form };
  for (const key of READING_FIELDS) {
    if (form[key] === before[key] && sent[key] === before[key] && !touched.has(key)) out[key] = after[key];
  }
  return out as EditState;
}

/** The reading fields a request changed from the scan it was sent over. */
export const changedFields = (sent: EditState, was: Scan): string[] => {
  const before = toEditState(was);
  return READING_FIELDS.filter((key) => sent[key] !== before[key]);
};

/**
 * What confirming sends: the identity corrections and the prices. Stock is
 * **not** here: whether the shop counts copies is step two, and the caller
 * merges it in; the presence of `stock` is what the backend reads as "track
 * this card".
 */
export function buildConfirmBody(scan: Scan, edit: EditState): ScanConfirmOptions {
  const corrections = buildCorrections(scan, edit);

  const body: ScanConfirmOptions = {};
  if (Object.keys(corrections).length > 0) body.corrections = corrections;

  const priceCents = inputToCents(edit.price);
  if (priceCents != null) body.priceCents = priceCents;
  const floorCents = inputToCents(edit.floorPrice);
  if (floorCents != null) body.floorPriceCents = floorCents;

  return body;
}
